import {
    resolveMeasureDefinition,
    changeConstructionSystem,
    type ConstructionSystem,
} from "@treegram/viewer-core";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";

import Button from "@/components/buttons/button";
import MeasureTypeEditor from "./measure-type-editor";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";

import { BaseDialogProps } from "@/components/dialogs/base-dialog";

import ClosableDialog from "@/components/dialogs/closable-dialog";
import editMeasureTypeAction from "@/pages/takeoff/layouts/measures/layouts/storey-measures/components/dialogs/edit-measure-type-action.ts";
import LoadingButton from "@/components/buttons/loading-button";

interface EditMeasureTypeFormElements extends HTMLFormControlsCollection {
    name: HTMLInputElement;
}

interface FormElements extends HTMLFormElement {
    readonly elements: EditMeasureTypeFormElements;
}

interface EditMeasureTypeProps extends BaseDialogProps {
    measureTypeId: string;
    representationOnly?: boolean;
    handleSave?: () => Promise<boolean | void>;
}

const EditMeasureTypeDialog: React.FC<EditMeasureTypeProps> = ({
    open,
    onClose,
    measureTypeId,
    handleSave,
    representationOnly = false,
}) => {
    const { ensureCanEdit, canEdit } = useProjectEditAccess();
    const [isPending, setIsPending] = useState(false);

    const { getMeasureType, editMeasureTypeLocally } =
        useProjectMeasureContext();

    const measureType = useMemo(
        () => getMeasureType(measureTypeId),
        [measureTypeId, getMeasureType],
    );

    const [system, setSystem] = useState<ConstructionSystem>(
        () =>
            resolveMeasureDefinition(
                measureType?.measureRepresentationId ?? "",
                measureType?.dataInformations,
            ).system,
    );
    useEffect(() => {
        if (open && measureType)
            setSystem(
                resolveMeasureDefinition(
                    measureType.measureRepresentationId,
                    measureType.dataInformations,
                ).system,
            );
    }, [open, measureType]);
    const fieldValues = useMemo(
        () =>
            measureType
                ? changeConstructionSystem(
                      measureType.measureRepresentationId,
                      measureType.dataInformations ?? {},
                      system,
                  )
                : {},
        [measureType, system],
    );

    const colorValue = useMemo(() => {
        const color = measureType?.dataInformations?.color as
            string | undefined;
        const alpha = measureType?.dataInformations?.alpha as
            number | undefined;
        return color && alpha != null
            ? `${color}${Math.round(alpha * 255)
                  .toString(16)
                  .padStart(2, "0")}`
            : undefined;
    }, [measureType?.dataInformations]);

    const [error, setError] = useState<string | undefined>(undefined);

    const resetForm = useCallback(() => {
        setError(undefined);
    }, []);

    const closeDialog = useCallback(() => {
        onClose(false);
        resetForm();
    }, [resetForm, onClose]);

    const handleSubmit = useCallback(
        async (event: React.FormEvent<FormElements>) => {
            event.preventDefault();
            if (!ensureCanEdit()) {
                return;
            }

            event.stopPropagation();

            if (measureType == null) return;

            const formData = new FormData(event.currentTarget);

            setIsPending(true);
            setError(undefined);

            try {
                const result = await editMeasureTypeAction({
                    measureType,
                    formData,
                });

                if (result.success && result.updatedMeasureType) {
                    editMeasureTypeLocally(result.updatedMeasureType);
                    if (handleSave) {
                        const saved = await handleSave();
                        if (saved === false) {
                            setError(
                                "Le type n’a pas été appliqué à tous les métrés. Réessayez l’enregistrement.",
                            );
                            return;
                        }
                    }
                    closeDialog();
                } else if (result.message) {
                    setError(result.message);
                }
            } catch (e: unknown) {
                setError(
                    e instanceof Error
                        ? e.message
                        : "Une erreur s'est produite lors de l'enregistrement.",
                );
            } finally {
                setIsPending(false);
            }
        },
        [ensureCanEdit, closeDialog, measureType, editMeasureTypeLocally, handleSave],
    );

    if (measureType == null) return null;

    return (
        <ClosableDialog
            open={open}
            onClose={closeDialog}
            title={`Modifier ${measureType.name}`}
            sizeClass="max-w-xl max-h-[90vh]"
            closeOnOutsideClick={!isPending}
            showCloseButton={!isPending}
        >
            <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
                {error && (
                    <div
                        role="alert"
                        className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700"
                    >
                        {error}
                    </div>
                )}
                <fieldset
                    disabled={isPending}
                    className="min-h-0 overflow-y-auto"
                >
                    <MeasureTypeEditor
                        disabled={!canEdit || (isPending)}
                        name={measureType.name}
                        shape={
                            resolveMeasureDefinition(
                                measureType.measureRepresentationId,
                                measureType.dataInformations,
                            ).shape
                        }
                        system={system}
                        values={fieldValues}
                        color={colorValue}
                        representationOnly={representationOnly}
                        onSystemChange={setSystem}
                    />
                </fieldset>
                <div className="mt-4 border-t border-gray-200 pt-4">
                    <p className="mb-3 rounded-md bg-gray-50 p-3 text-xs text-gray-600">
                        Les modifications s’appliqueront à tous les métrés de ce
                        type dans le projet, sur tous les niveaux. Les
                        propriétés modifiées remplaceront les valeurs propres
                        aux métrés.
                    </p>
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="ghost"
                            size="md"
                            disabled={isPending}
                            onClick={closeDialog}
                        >
                            Annuler
                        </Button>
                        <LoadingButton disabled={!canEdit}
                            variant="success"
                            size="md"
                            type="submit"
                            isLoading={isPending}
                            loadingBody="Enregistrement…"
                            body="Enregistrer"
                        />
                    </div>
                </div>
            </form>
        </ClosableDialog>
    );
};

export default EditMeasureTypeDialog;
